import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { StringDiffService } from '../../../../core/services/core.string-diff.service';

export interface TrackerItemEvent {
    id: number;
    eventType: string;
    eventDate: string;
    succeeded: boolean;
    section: string | null;
    refId: number | null;
    headRevision: string | null;
    label: string | null;
    note: string | null;
    extendedData: { jobName?: string; buildNumber?: number; jobId?: number; buildId?: number; url?: string; } | null;
    user: any;
    activityOrigin?: string;
    itemDate?: string;
    trackById?: string;
}

/**
 * Helper class for managing tracker item timeline functionality.
 */
@Injectable()
export class TrackeritemTimelineHelper {

    /**
     * Constructor for the TrackeritemTimelineHelper class.
     * @param stringDiffService - Service for comparing strings and generating diffs.
     */
    constructor(private stringDiffService: StringDiffService) {
    }

    private static toTimestamp(d: string): number {
        return d ? new Date(d).getTime() : 0;
    }

    /**
     * Checks if an item is an audit attachment.
     * @param item - The item to check.
     * @returns {boolean} True if the item is an audit attachment, false otherwise.
     */
    private isAuditAttachment(item): boolean {
        return _.some(_.get(item, 'data', []), (d: any) =>
            ['attachment_Added', 'attachment_Removed', 'filename'].includes(_.get(d, 'fieldname', ''))
        );
    }

    /**
     * Compares old and new audit trail values and returns the differences.
     * @param oldAuditTrailValue - The old audit trail value.
     * @param newAuditTrailValue - The new audit trail value.
     * @returns {Object} An object containing arrays of added and removed values.
     */
    private getAuditTrailValuesDiff(oldAuditTrailValue, newAuditTrailValue) {
        var oldAuditTrailValueArr = _.split(oldAuditTrailValue, ',');
        var newAuditTrailValueArr = _.split(newAuditTrailValue, ',');

        if (oldAuditTrailValue === '') {
            return { addedValues: newAuditTrailValueArr, removedValues: [] };
        }
        if (newAuditTrailValue === '') {
            return { removedValues: oldAuditTrailValueArr, addedValues: [] };
        }

        var addedValues = newAuditTrailValueArr.filter((item) => !oldAuditTrailValueArr.includes(item));
        var removedValues = oldAuditTrailValueArr.filter((item) => !newAuditTrailValueArr.includes(item));

        return { addedValues: addedValues, removedValues: removedValues };
    }

    /**
     * Maps an item to a standardized format for timeline display.
     * @param item - The item to map.
     * @param origin - The origin of the activity.
     * @param dateField - The field containing the date of the activity.
     * @returns {Object} A mapped item with standardized properties.
     */
    private mapItem(item, origin, dateField) {
        var ret = _.cloneDeep(item);
        ret.activityOrigin = origin;
        ret.itemDate = _.get(item, dateField, '');
        ret.trackById = origin + '-' + _.get(item, 'id', ret.itemDate);
        return ret;
    }

    /**
     * Maps commit data for timeline display.
     * @param ti_data - The tracker item data containing commit information.
     * @returns {Array} An array of mapped commit items.
     */
    private mapCommits(ti_data) {
        return _.map(_.get(ti_data, 'rel.commits', []),
            (item) => {
                return this.mapItem(item, 'commit', 'commitDate');
            }
        );
    }

    /**
     * Maps merge comment data for timeline display.
     * @param ti_data - The tracker item data containing merge comment information.
     * @returns {Array} An array of mapped merge comment items.
     */
    private mapMergeComments(ti_data) {
        return _.map(_.get(ti_data, 'rel.merge_comments', []),
            (item) => {
                return this.mapItem(item, 'merge_comment', 'postDate');
            }
        );
    }

    /**
     * Maps event records for timeline display.
     * Sets activityOrigin from item.eventType so existing display logic
     * (build glyph, CSS classes, template blocks) continues to work unchanged.
     *
     * Sets isStale=true when the event's headRevision does not match the
     * most recent commit on the tracker item, indicating that new commits
     * have landed since the event was recorded.
     * @param ti_data - The tracker item data containing event records.
     * @returns {Array} An array of mapped event items.
     */
    private mapEvents(ti_data): any[] {
        const commits = _.get(ti_data, 'rel.commits', []);
        const latestHash = _.maxBy(commits, (c: any) => TrackeritemTimelineHelper.toTimestamp(c.commitDate))?.commitHash ?? null;

        return _.map(_.get(ti_data, 'rel.events', []),
            (item) => {
                var mapped = this.mapItem(item, item.eventType, 'eventDate');
                mapped.user = item.user;
                mapped.isStale = !!(latestHash && item.headRevision && item.headRevision !== latestHash);
                return mapped;
            }
        );
    }

    /**
     * Maps followup messages for timeline display.
     * @param ti_data - The tracker item data containing followup messages.
     * @returns {Array} An array of mapped followup items.
     */
    private mapFollowups(ti_data) {
        return _.map(_.get(ti_data, 'rel.messages', []),
            (item) => {
                return this.mapItem(item, 'followup', 'adddate');
            }
        );
    }

    /**
     * Maps attachments and their audit trails for timeline display.
     * @param ti_data - The tracker item data containing attachment information.
     * @returns {Array} An array of mapped attachment and audit trail items.
     */
    private mapAttachments(ti_data) {
        var audits = _.filter(_.get(ti_data, 'rel.history', []), (item: any) => this.isAuditAttachment(item))
            .map(function (auditItem) {
                return _.map(auditItem.data, function (item) {
                    return {
                        activityDate: auditItem.activityDate,
                        user: auditItem.user,
                        fieldname: item.fieldname,
                        oldvalue: item.oldvalue,
                        newvalue: item.newvalue,
                        trackById: ["history", auditItem.activityDate, item.fieldname, item.newvalue].join('-')
                    };
                }).filter((item: any) => ['attachment_Added', 'attachment_Removed'].includes(item.fieldname));
            })
            .flat();

        const auditItems = audits
            .map((item) => {
                var ret: any = _.cloneDeep(item);
                ret.activityOrigin = 'audit_trail';
                ret.activityType = item.fieldname;
                ret.itemDate = item.activityDate;
                return ret;
            })
            .filter((item) => {
                if (item.activityType != 'attachment_Added') {
                    return true;
                }
                // Filter out attachment_Added when
                // there is no later attachment_Removed
                // for the same file name

                // This way, currently active attachments
                // don't get an extra/confusing added audit record,
                // just the thumbnail/link to the attachment.
                const laterRemoved = audits.find((other) => {
                    return other.fieldname == 'attachment_Removed' &&
                        other.oldvalue == item.newvalue &&
                        other.activityDate > item.activityDate;
                });
                if (laterRemoved) return true;
                return false;
            });

        const attachmentItems = _.map(
            _.get(ti_data, 'rel.attachments', []),
            (a) => {
                return {
                    activityOrigin: 'attachment',
                    activityType: 'attachment_Added',
                    itemDate: a.uploadedOn,
                    user: a.postedBy,
                    trackById: ["attachment", a.id].join('-'),
                    attachment: a
                };
            }
        );

        return attachmentItems.concat(auditItems);
    }

    /**
     * Maps activity log entries for timeline display.
     * @param ti_data - The tracker item data containing activity log information.
     * @returns {Array} An array of mapped activity log items.
     */
    private mapActivityLog(ti_data) {
        var activityLogs = _.filter(_.get(ti_data, 'rel.history', []), (item) => {
            return item.activityOrigin == 'activity_log' &&
                ['followup_delete', 'followup_edit', 'agent_completed', 'agent_incomplete'].includes(item.activityType);
        }).map((item) => {
            return this.mapItem(item, 'activity_log', 'activityDate');
        });

        return activityLogs;
    }

    /**
     * Generates a hash code for a given string value.
     * @param val - The string to hash.
     * @returns {string} A hexadecimal representation of the hash.
     */
    private hashCode(val) {
        var hash = 0;
        for (var i = 0; i < val.length; i++) {
            var character = val.charCodeAt(i);
            hash = ((hash << 5) - hash) + character;
            hash = hash & hash; // Convert to 32bit integer
        }
        return Math.abs(hash).toString(16);  //  Hex representation
    }

    /**
     * Removes duplicate items from a list based on their trackById.
     * @param list - The list of items to deduplicate.
     * @returns {Array} A deduplicated list of items.
     */
    private removeDupes(list) {
        return _.map(
            _.groupBy(list, 'trackById'),
            _.first
        );
    }

    /**
     * Maps field changes for timeline display.
     * @param ti_data - The tracker item data containing field changes.
     * @param _fields - The fields configuration for the tracker.
     * @returns {Array} An array of mapped field change items.
     */
    public mapFieldChanges(ti_data, _fields) {
        var timelineItem, strFieldname, strDiff, showDiff;
        var dataEntry = false, showEntry = false;
        var text = '';

        // We are only interested in audit trail changes
        // Ignore attachment changes - those are handled separately
        var items = _.filter(
                _.get(ti_data, 'rel.history', []),
                { activityOrigin: 'audit_trail' }
            ).filter((item) => { return !this.isAuditAttachment(item); });

        var ret = items.map((activityItem, key1) => {
            dataEntry = false;
            timelineItem = _.cloneDeep(activityItem);

            var data = _.get(timelineItem, 'data', []);

            _.forEach(data, (dataItem, key2) => {
                if (!_.isEmpty(dataItem)) {
                    strDiff = '';
                    text = '';
                    showEntry = true;

                    // Skip timeline data items that don't have the required basic data.
                    if (!dataItem?.fieldname) {
                        return false;
                    }

                    var field = _.get(_fields, _.get(dataItem, 'fieldname', ''), false);
                    strFieldname = _.get(field, "fieldName", _.get(dataItem, 'fieldname', ''));

                    // Field Type 6 = SELECT
                    if (_.get(field, 'fieldType', 0) == 6) {
                        strDiff = this.stringDiffService.diffString(
                            _.get(dataItem, 'oldvalue', 'None') || '',
                            _.get(dataItem, 'newvalue', 'None') || ''
                        );
                    }

                    if (['submitted_by', 'status_id', 'approval', 'build', 'deployment'].includes(strFieldname)) {
                        showEntry = false;
                    }

                    if (strFieldname == 'dependent-on' || strFieldname == 'dependent-uponby' || strFieldname == 'blocking_dependency') {

                        var atValuesDiff = this.getAuditTrailValuesDiff(dataItem.oldvalue, dataItem.newvalue);

                        var atValueAdded = false;
                        if (atValuesDiff.addedValues.length) {  //relations are updated one at a time
                            atValueAdded = true;
                        }

                        if (strFieldname == 'dependent-on') {
                            if (atValueAdded) {
                                text = 'This task is dependent on task #' + atValuesDiff.addedValues[0];
                            } else {
                                text = 'This task is no longer dependent on task #' + atValuesDiff.removedValues[0];
                            }
                        }

                        if (strFieldname == 'dependent-uponby') {
                            if (atValueAdded) {
                                text = 'This task is depended upon by task #' + atValuesDiff.addedValues[0];
                            } else {
                                text = 'This task is no longer depended upon by task #' + atValuesDiff.removedValues[0];
                            }
                        }

                        if (strFieldname == 'blocking_dependency') {
                            if (atValueAdded) {
                                text = 'This task is blocked by task #' + atValuesDiff.addedValues[0];

                            } else {
                                text = 'This task is no longer blocked by task #' + atValuesDiff.removedValues[0];
                            }
                        }
                    }

                    if (showEntry) {
                        timelineItem.data[key2].fieldname = strFieldname;
                        timelineItem.data[key2].diff = strDiff;
                        timelineItem.data[key2].text = text;
                        dataEntry = true;
                    } else {
                        // Remove the data item from the containing array
                        timelineItem.data.splice(key2, 1);
                        dataEntry = true;
                    }
                }
            });

            timelineItem.trackById = timelineItem.activityOrigin + '-' + timelineItem.activityDate + '-' + timelineItem.activityType;

            //Do not include updates with no data to show
            if (timelineItem.data.length) {
                timelineItem.itemDate = _.get(timelineItem, 'activityDate', '');
                return timelineItem;
            } else if (!dataEntry) {
                timelineItem.itemDate = _.get(timelineItem, 'activityDate', '');
                return timelineItem;
            } else {
                return null;
            }
        });

        return ret;
    }

    /**
     * Creates a timeline from tracker item data.
     * @param ti_data - The tracker item data.
     * @param tracker - The tracker configuration.
     * @returns {Array} A sorted array of timeline items.
     */
    public createTimeline(ti_data, tracker) {
        // Filter removes non-objects, concat creates a single array
        var allItems = _.filter(
            this.mapAttachments(ti_data)
                .concat(this.mapCommits(ti_data), this.mapMergeComments(ti_data), this.mapEvents(ti_data), this.mapFollowups(ti_data), this.mapFieldChanges(ti_data, _.get(tracker, 'fields', {})), this.mapActivityLog(ti_data))
        );

        // reverse makes the sorting newest-first
        return _.reverse(
            // sortBy puts the unified array into the right date order
            _.sortBy(
                // map converts the long-form trackById to a shorter hash
                this.removeDupes(allItems)
                    .map((item) => {
                        item.trackById = this.hashCode(item.trackById);
                        return item;
                    }), [
                        (item: any) => TrackeritemTimelineHelper.toTimestamp(item.itemDate),
                        (item: any) => item.id || 0
                    ]
            )
        );
    }

    removeFollowup(ti_data: any, item: any): any {
        _.set(
            ti_data, 'rel.messages',
            _.filter(
                _.get(ti_data, 'rel.messages', []),
                (msg) => msg.id != item.id
            )
        );
        return ti_data;
    }
}
